import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Container,
  TextField,
  Button,
  Typography,
  Alert,
  Paper,
  CircularProgress,
  InputAdornment,
  IconButton,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
} from '@mui/material';
import {
  Visibility,
  VisibilityOff,
  CheckCircle,
  Cancel,
} from '@mui/icons-material';
import { authService } from '../../services/api';
import LanguageSelector from '../../components/LanguageSelector/LanguageSelector';
import logoGM from '../../assets/logo_gm.png';

const Registration = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password1: '',
    password2: '',
  });

  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [error, setError] = useState('');
  const [backendErrors, setBackendErrors] = useState([]);
  const [success, setSuccess] = useState('');
  const [loading, setLoading] = useState(false);
  const [touched, setTouched] = useState({});

  // Password validations
  const passwordValidations = {
    minLength: formData.password1.length >= 9,
    hasUpperCase: /[A-Z]/.test(formData.password1),
    hasLowerCase: /[a-z]/.test(formData.password1),
    hasNumber: /[0-9]/.test(formData.password1),
    hasSpecialChar: /[!@#$%^&*(),.?":{}|<>]/.test(formData.password1),
  };

  const allPasswordValidationsPassed = Object.values(passwordValidations).every(Boolean);
  const passwordsMatch = formData.password1 === formData.password2 && formData.password2 !== '';

  const handleChange = (field) => (e) => {
    setFormData({ ...formData, [field]: e.target.value });
    setError('');
    setBackendErrors([]);
  };

  const handleBlur = (field) => () => {
    setTouched({ ...touched, [field]: true });
  };

  const validateEmail = (email) => {
    const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return re.test(email);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setBackendErrors([]);
    setSuccess('');

    // Basic validations
    if (!formData.name || !formData.email || !formData.password1 || !formData.password2) {
      setError(t('registration.fillAllFields'));
      return;
    }

    if (!validateEmail(formData.email)) {
      setError(t('registration.invalidEmail'));
      return;
    }

    if (!allPasswordValidationsPassed) {
      setError(t('registration.passwordRequirements'));
      return;
    }

    if (formData.password1 !== formData.password2) {
      setError(t('registration.passwordMismatch'));
      return;
    }

    setLoading(true);

    try {
      const response = await authService.register(
        formData.email,
        formData.password1,
        formData.password2,
        formData.name
      );

      setSuccess(t('registration.success'));

      // Redirect to login after 2 seconds
      setTimeout(() => {
        navigate('/login');
      }, 2000);
    } catch (err) {
      console.error('Registration error:', err);

      // Check if backend returned validation errors
      if (err.response?.data) {
        const errors = [];
        const data = err.response.data;

        // Handle password2 errors (most common for password validation)
        if (data.password2) {
          errors.push(...(Array.isArray(data.password2) ? data.password2 : [data.password2]));
        }

        // Handle password1 errors
        if (data.password1) {
          errors.push(...(Array.isArray(data.password1) ? data.password1 : [data.password1]));
        }

        // Handle email errors
        if (data.email) {
          errors.push(...(Array.isArray(data.email) ? data.email : [data.email]));
        }

        // Handle name errors
        if (data.name) {
          errors.push(...(Array.isArray(data.name) ? data.name : [data.name]));
        }

        // Handle non_field_errors
        if (data.non_field_errors) {
          errors.push(...(Array.isArray(data.non_field_errors) ? data.non_field_errors : [data.non_field_errors]));
        }

        // Handle detail errors
        if (data.detail) {
          errors.push(data.detail);
        }

        if (errors.length > 0) {
          setBackendErrors(errors);
        } else {
          setError(t('registration.error'));
        }
      } else {
        setError(t('registration.error'));
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: 'background.paper',
        position: 'relative',
      }}
    >
      {/* Language Selector - Top Right */}
      <Box
        sx={{
          position: 'absolute',
          top: 20,
          right: 20,
        }}
      >
        <LanguageSelector />
      </Box>

      <Container maxWidth="sm">
        <Paper
          elevation={3}
          sx={{
            p: 4,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            backgroundColor: 'background.default',
            borderRadius: 2,
          }}
        >
          <Box
            component="img"
            src={logoGM}
            alt="GM Logo"
            sx={{
              width: 120,
              height: 120,
              mb: 2,
              objectFit: 'contain',
            }}
          />

          <Typography
            component="h1"
            variant="h5"
            sx={{
              mb: 1,
              fontWeight: 600,
              color: 'text.primary',
            }}
          >
            {t('registration.title')}
          </Typography>

          <Typography
            variant="body2"
            sx={{
              mb: 3,
              color: 'text.secondary',
            }}
          >
            {t('registration.subtitle')}
          </Typography>

          <Box
            component="form"
            onSubmit={handleSubmit}
            sx={{ width: '100%' }}
          >
            {error && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {error}
              </Alert>
            )}

            {backendErrors.length > 0 && (
              <Alert severity="error" sx={{ mb: 2 }}>
                <Box component="ul" sx={{ margin: 0, paddingLeft: 2 }}>
                  {backendErrors.map((err, index) => (
                    <li key={index}>{err}</li>
                  ))}
                </Box>
              </Alert>
            )}

            {success && (
              <Alert severity="success" sx={{ mb: 2 }}>
                {success}
              </Alert>
            )}

            <TextField
              margin="normal"
              required
              fullWidth
              id="name"
              label={t('registration.name')}
              name="name"
              autoComplete="name"
              autoFocus
              value={formData.name}
              onChange={handleChange('name')}
              onBlur={handleBlur('name')}
              disabled={loading}
              sx={{ mb: 2 }}
            />

            <TextField
              margin="normal"
              required
              fullWidth
              id="email"
              label={t('registration.email')}
              name="email"
              autoComplete="email"
              value={formData.email}
              onChange={handleChange('email')}
              onBlur={handleBlur('email')}
              disabled={loading}
              error={touched.email && !validateEmail(formData.email) && formData.email !== ''}
              helperText={
                touched.email && !validateEmail(formData.email) && formData.email !== ''
                  ? t('registration.invalidEmail')
                  : ''
              }
              sx={{ mb: 2 }}
            />

            <TextField
              margin="normal"
              required
              fullWidth
              name="password1"
              label={t('registration.password')}
              type={showPassword ? 'text' : 'password'}
              id="password1"
              autoComplete="new-password"
              value={formData.password1}
              onChange={handleChange('password1')}
              onBlur={handleBlur('password1')}
              disabled={loading}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() => setShowPassword(!showPassword)}
                      edge="end"
                    >
                      {showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
              sx={{ mb: 1 }}
            />

            {/* Password Requirements */}
            {formData.password1 && (
              <Box sx={{ mb: 2, pl: 1 }}>
                <List dense disablePadding>
                  <ListItem disableGutters sx={{ py: 0.25 }}>
                    <ListItemIcon sx={{ minWidth: 32 }}>
                      {passwordValidations.minLength ? (
                        <CheckCircle fontSize="small" color="success" />
                      ) : (
                        <Cancel fontSize="small" color="error" />
                      )}
                    </ListItemIcon>
                    <ListItemText
                      primary={t('registration.minLength')}
                      primaryTypographyProps={{ variant: 'caption' }}
                    />
                  </ListItem>
                  <ListItem disableGutters sx={{ py: 0.25 }}>
                    <ListItemIcon sx={{ minWidth: 32 }}>
                      {passwordValidations.hasUpperCase ? (
                        <CheckCircle fontSize="small" color="success" />
                      ) : (
                        <Cancel fontSize="small" color="error" />
                      )}
                    </ListItemIcon>
                    <ListItemText
                      primary={t('registration.hasUpperCase')}
                      primaryTypographyProps={{ variant: 'caption' }}
                    />
                  </ListItem>
                  <ListItem disableGutters sx={{ py: 0.25 }}>
                    <ListItemIcon sx={{ minWidth: 32 }}>
                      {passwordValidations.hasLowerCase ? (
                        <CheckCircle fontSize="small" color="success" />
                      ) : (
                        <Cancel fontSize="small" color="error" />
                      )}
                    </ListItemIcon>
                    <ListItemText
                      primary={t('registration.hasLowerCase')}
                      primaryTypographyProps={{ variant: 'caption' }}
                    />
                  </ListItem>
                  <ListItem disableGutters sx={{ py: 0.25 }}>
                    <ListItemIcon sx={{ minWidth: 32 }}>
                      {passwordValidations.hasNumber ? (
                        <CheckCircle fontSize="small" color="success" />
                      ) : (
                        <Cancel fontSize="small" color="error" />
                      )}
                    </ListItemIcon>
                    <ListItemText
                      primary={t('registration.hasNumber')}
                      primaryTypographyProps={{ variant: 'caption' }}
                    />
                  </ListItem>
                  <ListItem disableGutters sx={{ py: 0.25 }}>
                    <ListItemIcon sx={{ minWidth: 32 }}>
                      {passwordValidations.hasSpecialChar ? (
                        <CheckCircle fontSize="small" color="success" />
                      ) : (
                        <Cancel fontSize="small" color="error" />
                      )}
                    </ListItemIcon>
                    <ListItemText
                      primary={t('registration.hasSpecialChar')}
                      primaryTypographyProps={{ variant: 'caption' }}
                    />
                  </ListItem>
                </List>
              </Box>
            )}

            <TextField
              margin="normal"
              required
              fullWidth
              name="password2"
              label={t('registration.confirmPassword')}
              type={showConfirmPassword ? 'text' : 'password'}
              id="password2"
              autoComplete="new-password"
              value={formData.password2}
              onChange={handleChange('password2')}
              onBlur={handleBlur('password2')}
              disabled={loading}
              error={touched.password2 && !passwordsMatch && formData.password2 !== ''}
              helperText={
                touched.password2 && !passwordsMatch && formData.password2 !== ''
                  ? t('registration.passwordMismatch')
                  : ''
              }
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                      edge="end"
                    >
                      {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
              sx={{ mb: 3 }}
            />

            <Button
              type="submit"
              fullWidth
              variant="contained"
              disabled={loading || !allPasswordValidationsPassed || !passwordsMatch}
              sx={{
                py: 1.5,
                fontSize: '1rem',
                fontWeight: 600,
                mb: 2,
              }}
            >
              {loading ? (
                <CircularProgress size={24} color="inherit" />
              ) : (
                t('registration.registerButton')
              )}
            </Button>

            <Box sx={{ textAlign: 'center' }}>
              <Typography variant="body2" color="text.secondary">
                {t('registration.alreadyHaveAccount')}{' '}
                <Link
                  to="/login"
                  style={{
                    color: 'inherit',
                    fontWeight: 600,
                    textDecoration: 'none',
                  }}
                >
                  {t('registration.loginLink')}
                </Link>
              </Typography>
            </Box>
          </Box>
        </Paper>
      </Container>
    </Box>
  );
};

export default Registration;
